import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import { Modal, Button } from "react-bootstrap";
import { Icon } from "@iconify/react/dist/iconify.js";
import Loader from "../Loader";
import { isUserAuthorized } from "../../utils/authorize";
import { 
  fetchPriceRanges, 
  addPriceRange, 
  updatePriceRange, 
  deletePriceRange 
} from "../../features/priceRange/priceRangeSlice";

const PriceRangeLayer = () => {
  const dispatch = useDispatch();
  const role = useSelector((state) => state?.cred?.user?.role);
  const status = useSelector((state) => state.priceRange?.status);
  const error = useSelector((state) => state.priceRange?.error);
  const priceRanges = useSelector((state) => state.priceRange?.items);

  const [showModal, setShowModal] = useState(false);
  const [editingRange, setEditingRange] = useState(null);
  const [formData, setFormData] = useState({
    fromPrice: "",
    toPrice: "",
    bookingPricePercentage: "",
    repairCostPercentage: "",
    type: "booking" // or "repair"
  });

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchPriceRanges());
    }
  }, [dispatch, status]);

  const handleOpenModal = (range = null) => {
    if (range) {
      setEditingRange(range);
      setFormData({
        fromPrice: range.fromPrice.toString(),
        toPrice: range.toPrice.toString(),
        bookingPricePercentage: range.bookingPricePercentage?.toString() || "",
        repairCostPercentage: range.repairCostPercentage?.toString() || "",
        type: range.type || "booking"
      });
    } else {
      setEditingRange(null);
      setFormData({
        fromPrice: "",
        toPrice: "",
        bookingPricePercentage: "",
        repairCostPercentage: "",
        type: "booking"
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRange(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    // Validate form data
    if (!formData.fromPrice || !formData.toPrice || 
        !formData.bookingPricePercentage || !formData.repairCostPercentage) {
      toast.error("Please fill in all fields");
      return;
    }

    if (parseFloat(formData.fromPrice) >= parseFloat(formData.toPrice)) {
      toast.error("From price must be less than to price");
      return;
    }

    if (parseFloat(formData.bookingPricePercentage) < 0 || parseFloat(formData.bookingPricePercentage) > 100) {
      toast.error("Booking price percentage must be between 0 and 100");
      return;
    }

    if (parseFloat(formData.repairCostPercentage) < 0 || parseFloat(formData.repairCostPercentage) > 100) {
      toast.error("Repair cost percentage must be between 0 and 100");
      return;
    }

    try {
      const priceRangeData = {
        fromPrice: parseFloat(formData.fromPrice),
        toPrice: parseFloat(formData.toPrice),
        bookingPricePercentage: parseFloat(formData.bookingPricePercentage),
        repairCostPercentage: parseFloat(formData.repairCostPercentage),
        type: formData.type
      };

      if (editingRange) {
        await dispatch(updatePriceRange({ 
          id: editingRange._id || editingRange.id, 
          updatedData: priceRangeData 
        }));
        toast.success("Price range updated successfully");
        window.location.reload()
      } else {
        await dispatch(addPriceRange(priceRangeData));
        toast.success("Price range added successfully");
        window.location.reload()
      }
      
      handleCloseModal();
    } catch (error) {
      toast.error(`Error ${editingRange ? 'updating' : 'adding'} price range: ${error.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this price range?")) {
      try {
        await dispatch(deletePriceRange(id));
        toast.success("Price range deleted successfully");
        window.location.reload()
      } catch (error) {
        toast.error(`Error deleting price range: ${error.message}`);
      }
    }
  };

  if (status === "loading") return <Loader />;
  // if (status === "failed") return <p>Error: {error}</p>;
  if (status === "failed") return <p>{error}</p>;

  return (
    <div className="row gy-4">
      <div className="col-12">
        <div className="card shadow-sm border-0 radius-12">
          <div className="card-header bg-transparent border-bottom d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Price Range Management</h5>
            {(role === 'admin' || isUserAuthorized) && (
              <button 
                className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                onClick={() => handleOpenModal()}
              >
                <Icon icon="mdi:plus" className="icon fs-4" />
                Add Price Range
              </button>
            )}
          </div>
          <div className="card-body">
            {priceRanges && priceRanges.length === 0 ? (
              <div className="text-center py-5">
                <Icon icon="mdi:currency-usd-off" className="icon text-muted" style={{ fontSize: '3rem' }} />
                <p className="text-muted mt-3">No price ranges configured yet</p>
                {(role === 'admin' || isUserAuthorized) && (
                  <button 
                    className="btn btn-primary"
                    onClick={() => handleOpenModal()}
                  >
                    Add First Price Range
                  </button>
                )}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Price Range</th>
                      <th>Booking Price %</th>
                      <th>Repair Cost %</th>
                      <th>Type</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {priceRanges && priceRanges.map((range) => (
                      <tr key={range._id || range.id}>
                        <td>
                          <strong>{range.fromPrice?.toLocaleString()} - {range.toPrice?.toLocaleString()} AED</strong>
                        </td>
                        <td>
                          <span className="badge bg-success">{range.bookingPricePercentage || 0}%</span>
                        </td>
                        <td>
                          <span className="badge bg-info">{range.repairCostPercentage || 0}%</span>
                        </td>
                        <td>
                          <span className={`badge bg-${(range.type || 'booking') === 'booking' ? 'primary' : 'warning'}`}>
                            {(range.type || 'booking').charAt(0).toUpperCase() + (range.type || 'booking').slice(1)}
                          </span>
                        </td>
                        <td>
                          {(role === 'admin' || isUserAuthorized) && (
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleOpenModal(range)}
                              >
                                <Icon icon="mdi:pencil" className="icon fs-4" />
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(range._id || range.id)}
                              >
                                <Icon icon="mdi:delete" className="icon fs-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Price Range Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingRange ? 'Edit Price Range' : 'Add Price Range'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label htmlFor="fromPrice" className="form-label">From Price (AED)</label>
                <input
                  type="number"
                  className="form-control"
                  id="fromPrice"
                  name="fromPrice"
                  value={formData.fromPrice}
                  onChange={handleInputChange}
                  placeholder="Enter from price"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label htmlFor="toPrice" className="form-label">To Price (AED)</label>
                <input
                  type="number"
                  className="form-control"
                  id="toPrice"
                  name="toPrice"
                  value={formData.toPrice}
                  onChange={handleInputChange}
                  placeholder="Enter to price"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>
          
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label htmlFor="bookingPricePercentage" className="form-label">Booking Price Percentage (%)</label>
                <input
                  type="number"
                  className="form-control"
                  id="bookingPricePercentage"
                  name="bookingPricePercentage"
                  value={formData.bookingPricePercentage}
                  onChange={handleInputChange}
                  placeholder="Enter booking price percentage"
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label htmlFor="repairCostPercentage" className="form-label">Repair Cost Percentage (%)</label>
                <input
                  type="number"
                  className="form-control"
                  id="repairCostPercentage"
                  name="repairCostPercentage"
                  value={formData.repairCostPercentage}
                  onChange={handleInputChange}
                  placeholder="Enter repair cost percentage"
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* <div className="mb-3">
            <label htmlFor="type" className="form-label">Type</label>
            <select
              className="form-select"
              id="type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
            >
              <option value="booking">Booking</option>
              <option value="repair">Repair</option>
            </select>
          </div> */}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {editingRange ? 'Update Price Range' : 'Add Price Range'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
    </div>
  );
};

export default PriceRangeLayer; 
